import { useEffect, useState } from "react";
import { Loader2, Printer, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { api, apiError, fmtDateTime } from "@/lib/api";
import { AiNotice, MatchRing, ScoreBar, SkillChip, StatusBadge } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { SelectField } from "@/components/forms/Fields";

const NA = (v) => (v === null || v === undefined || v === "" ? "Not available" : v);

function printReport(report) {
  const b = report.match?.breakdown || {};
  const rows = Object.entries(b)
    .map(([k, v]) => `<tr><td>${k.replace(/_/g, " ")}</td><td>${v == null ? "Not available" : v + "%"}</td></tr>`)
    .join("");
  const panel = (report.panel_feedback || [])
    .map((p) => `<li>${NA(p.round)} (${NA(p.type)}) · ${NA(p.interviewer)} · overall ${NA(p.overall)}/5 · ${NA(p.recommendation)}</li>`)
    .join("") || "<li>Not available</li>";
  const html = `<!doctype html><html><head><title>Candidate AI report · ${report.candidate.name}</title>
  <style>
    body{font-family:Inter,Arial,sans-serif;color:#0f172a;margin:36px;font-size:13px;line-height:1.55}
    h1{font-size:20px;margin:0 0 4px} h2{font-size:12px;letter-spacing:.12em;text-transform:uppercase;color:#64748b;margin:26px 0 8px}
    .muted{color:#64748b;font-size:11px} table{border-collapse:collapse;width:320px}
    td{border-bottom:1px solid #e2e8f0;padding:5px 0;text-transform:capitalize}
    .score{font-size:34px;font-weight:600} .box{border:1px solid #e2e8f0;border-radius:6px;padding:14px;margin-top:10px}
    ul{margin:6px 0 0 18px;padding:0} .brand{font-size:11px;letter-spacing:.2em;color:#2563eb}
  </style></head><body>
  <p class="brand">DIGNIFYD TALENT CONNECT</p>
  <h1>${report.candidate.name} — candidate AI report</h1>
  <p class="muted">${NA(report.candidate.code)} · ${NA(report.candidate.designation)} at ${NA(report.candidate.company)} ·
  ${NA(report.candidate.experience_years)} yrs · ${NA(report.candidate.location)}</p>
  <h2>Role assessed</h2>
  <p>${NA(report.job.title)} — ${NA(report.job.client_name)} (${NA(report.job.job_code)}), ${NA(report.job.location)}</p>
  <h2>Overall match</h2>
  <p class="score">${report.match ? report.match.overall + "%" : "Not available"}</p>
  <p class="muted">${report.match ? report.match.band : ""}</p>
  <table>${rows}</table>
  <h2>Matched skills</h2><p>${(report.match?.matched_skills || []).join(", ") || "Not available"}</p>
  <h2>Missing skills</h2><p>${(report.match?.missing_skills || []).join(", ") || "None"}</p>
  <h2>Potential concerns</h2><ul>${(report.match?.concerns || []).map((c) => `<li>${c}</li>`).join("") || "<li>None flagged</li>"}</ul>
  <h2>Compensation & availability</h2>
  <p>Current CTC ${NA(report.candidate.current_ctc)} · Expected ${NA(report.candidate.expected_ctc)} ·
  Notice ${NA(report.candidate.notice_period)} · Availability ${NA(report.candidate.availability)}</p>
  <h2>Interview panel feedback</h2><ul>${panel}</ul>
  <h2>AI assessment</h2><div class="box">${NA(report.narrative)}</div>
  <h2>Disclaimer</h2><p class="muted">${report.disclaimer}</p>
  <p class="muted">Generated ${fmtDateTime(report.generated_at)} by ${report.generated_by}.</p>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return toast.error("Allow pop-ups to print this report");
  w.document.write(html);
  w.document.close();
  w.focus();
  w.print();
}

export default function CandidateReport({ open, onOpenChange, candidateId, applications = [], defaultJobId }) {
  const [jobId, setJobId] = useState(defaultJobId || null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (job) => {
    setBusy(true);
    setReport(null);
    try {
      const { data } = await api.get(`/ai/candidate/${candidateId}/report`, {
        params: job ? { job_id: job } : {},
      });
      setReport(data);
      setJobId(data.job?.id || null);
    } catch (e) {
      toast.error(apiError(e, "Could not build the report"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (open) load(defaultJobId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, candidateId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl border-slate-200 bg-white p-0">
        <DialogHeader className="border-b border-slate-200 px-6 py-5">
          <DialogTitle className="flex items-center gap-2 text-lg font-medium text-slate-900">
            <Sparkles className="h-4 w-4 text-violet-600" /> Candidate AI report
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Built only from the stored profile, the role requirements and captured panel feedback.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(92vh-190px)]">
          <div className="space-y-5 px-6 py-5">
            {applications.length > 1 && (
              <SelectField
                label="Assess against"
                value={jobId}
                onChange={(v) => load(v)}
                testid="report-job-select"
                options={applications.map((a) => ({ value: a.job_id, label: `${a.job_title} · ${a.client_name || "Client"}` }))}
              />
            )}

            {busy || !report ? (
              <div className="space-y-3">
                <Skeleton className="h-24 bg-slate-100" />
                <Skeleton className="h-40 bg-slate-100" />
                <Skeleton className="h-24 bg-slate-100" />
                {busy && (
                  <p className="flex items-center gap-2 text-xs text-slate-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Compiling the assessment…
                  </p>
                )}
              </div>
            ) : (
              <div data-testid="candidate-report-body" className="space-y-5">
                <div className="surface flex flex-wrap items-start justify-between gap-4 rounded-md p-5">
                  <div className="min-w-0">
                    <p className="label-xs">{NA(report.candidate.code)}</p>
                    <h3 className="mt-1 text-lg font-medium text-slate-900">{report.candidate.name}</h3>
                    <p className="text-xs text-slate-500">
                      {NA(report.candidate.designation)} · {NA(report.candidate.company)} ·{" "}
                      {NA(report.candidate.experience_years)} yrs · {NA(report.candidate.location)}
                    </p>
                    <p className="mt-2 text-xs text-slate-600">
                      Assessed against <span className="text-slate-900">{NA(report.job.title)}</span>
                      {report.job.client_name ? ` for ${report.job.client_name}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <MatchRing score={report.match?.overall} testid="report-match-ring" />
                    {report.match && <StatusBadge value={report.match.band} />}
                  </div>
                </div>

                {report.match ? (
                  <>
                    <div className="surface grid gap-x-10 gap-y-3 rounded-md p-5 sm:grid-cols-2">
                      {Object.entries(report.match.breakdown).map(([k, v]) => (
                        <ScoreBar key={k} label={k} value={v} />
                      ))}
                    </div>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div className="surface rounded-md p-4">
                        <p className="label-xs mb-2">Matched skills</p>
                        <div className="flex flex-wrap gap-1">
                          {report.match.matched_skills.length
                            ? report.match.matched_skills.map((s) => <SkillChip key={s} skill={s} tone="matched" />)
                            : <span className="text-xs text-slate-400">Not available</span>}
                        </div>
                      </div>
                      <div className="surface rounded-md p-4">
                        <p className="label-xs mb-2">Missing skills</p>
                        <div className="flex flex-wrap gap-1">
                          {report.match.missing_skills.length
                            ? report.match.missing_skills.map((s) => <SkillChip key={s} skill={s} tone="missing" />)
                            : <span className="text-xs text-slate-400">None</span>}
                        </div>
                      </div>
                      <div className="surface rounded-md p-4">
                        <p className="label-xs mb-2">Potential concerns</p>
                        {report.match.concerns.length ? (
                          <ul className="space-y-1.5">
                            {report.match.concerns.map((c, i) => (
                              <li key={i} className="text-[11px] leading-relaxed text-amber-800/90">{c}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-xs text-slate-400">None flagged</span>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    This candidate is not linked to a job yet, so no match score could be computed. Add them to a
                    requirement to score the fit.
                  </p>
                )}

                <div className="surface grid gap-4 rounded-md p-5 sm:grid-cols-4">
                  {[["Current CTC", report.candidate.current_ctc], ["Expected CTC", report.candidate.expected_ctc],
                    ["Notice period", report.candidate.notice_period], ["Availability", report.candidate.availability]].map(([k, v]) => (
                    <div key={k}>
                      <p className="label-xs">{k}</p>
                      <p className="font-mono-num mt-1 text-xs text-slate-800">{NA(v)}</p>
                    </div>
                  ))}
                </div>

                <div className="surface rounded-md p-5">
                  <p className="label-xs mb-3">Interview panel feedback</p>
                  {report.panel_feedback.length === 0 ? (
                    <p className="text-xs text-slate-400">Not available</p>
                  ) : (
                    <ul className="space-y-3">
                      {report.panel_feedback.map((p, i) => (
                        <li key={i} className="border-l border-slate-200 pl-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-slate-800">{NA(p.round)} · {NA(p.type)}</span>
                            {p.recommendation && <StatusBadge value={p.recommendation} />}
                            {p.overall && <span className="font-mono-num text-[11px] text-slate-500">{p.overall}/5</span>}
                          </div>
                          <p className="text-[11px] text-slate-500">{NA(p.interviewer)}</p>
                          {p.strengths && <p className="mt-1 text-[11px] text-slate-600">Strengths: {p.strengths}</p>}
                          {p.concerns && <p className="text-[11px] text-slate-600">Concerns: {p.concerns}</p>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="surface rounded-md p-5">
                  <p className="label-xs mb-3">AI assessment</p>
                  <p className="text-sm leading-relaxed text-slate-700" data-testid="report-narrative">
                    {NA(report.narrative)}
                  </p>
                  <AiNotice text={report.disclaimer} />
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="flex-col-reverse gap-2 border-t border-slate-200 bg-white px-6 py-4 sm:flex-row">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-500">Close</Button>
          <Button
            onClick={() => printReport(report)}
            disabled={!report}
            data-testid="report-print-button"
            className="gap-2 rounded-full bg-violet-600 px-5 hover:bg-violet-500"
          >
            <Printer className="h-3.5 w-3.5" /> Print / save as PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
